import React, { useEffect, useState } from 'react';
import { useParams }                  from 'react-router-dom';
import { getPublicReport }             from '../api/sessions';

/**
 * PublicReportPage - Phase 8.
 *
 * Renders a session report from a signed public share token. No authentication
 * required - the token itself authorizes access. Designed to be linkable from
 * email or chat, and intentionally minimalist (no app shell, no nav).
 */
const SEVERITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

function severityClass(sev) {
  switch ((sev || '').toUpperCase()) {
    case 'CRITICAL': return 'bg-red-100 text-red-800 border-red-200';
    case 'HIGH':     return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'MEDIUM':   return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'LOW':      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    default:         return 'bg-slate-100 text-slate-700 border-slate-200';
  }
}

function fmt(iso) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function PublicReportPage() {
  const { token }                  = useParams();
  const [report,  setReport]       = useState(null);
  const [loading, setLoading]      = useState(true);
  const [error,   setError]        = useState('');

  useEffect(() => {
    let cancelled = false;
    console.log('[PublicReport] loading via share token');
    setLoading(true);
    getPublicReport(token)
      .then((res) => { if (!cancelled) setReport(res.data); })
      .catch((err) => {
        if (cancelled) return;
        console.error('[PublicReport] load failed:', err.message);
        setError(err.message || 'Share link is invalid or expired.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-canvas">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="bg-white border border-red-200 rounded-xl p-8 max-w-md text-center space-y-3 shadow">
          <p className="text-red-700 font-semibold text-lg">Report unavailable</p>
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const sevCounts = SEVERITIES.reduce((acc, s) => {
    acc[s] = (report?.findings || []).filter(f => (f.severity || '').toUpperCase() === s).length;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-canvas py-10 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto card p-6 sm:p-8 space-y-8">
        <header className="border-b border-line pb-5">
          <p className="text-xs text-teal-600 uppercase tracking-wider font-semibold">Samastix • Public Report</p>
          <h1 className="font-heading text-2xl font-bold text-ink mt-2 font-mono">#{report?.sessionCode}</h1>
          <p className="text-sm text-ink-muted mt-1">
            Started {fmt(report?.startedAt)} • Ended {fmt(report?.endedAt)}
          </p>
        </header>

        {/* Headline metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {/* I-P0-B: server integrity status from normalized, unreviewed signals (not a verdict). */}
          <Metric label="Integrity Status (unreviewed signals)" value={
            ({ NONE: 'No review signals', MEDIUM: 'Review signals (medium)', HIGH: 'Review signals (high)' })[
              (report?.integrityStatus || '').toUpperCase()] ?? 'Not evaluated'} />
          <Metric label="Integrity Signals (unreviewed)" value={report?.totalFindings ?? (report?.findings?.length ?? 0)} />
          <Metric label="Status" value={report?.status ?? '—'} />
        </div>

        {/* Parties */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Host"      value={report?.hostEmail} />
          <Field label="Candidate" value={report?.candidateEmail} />
        </section>

        {/* Severity counts */}
        <section>
          <h2 className="text-sm font-semibold text-ink-secondary uppercase tracking-wider mb-3">Integrity Signals by Severity (unreviewed)</h2>
          <p className="text-xs text-ink-muted mb-3">
            Integrity signals are automated, unreviewed detector observations. They can be caused by
            legitimate software, hardware or environment and are not a finding of misconduct.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {SEVERITIES.map((s) => (
              <div key={s} className="bg-canvas-subtle border border-line rounded-lg p-3 text-center">
                <p className={`text-2xl font-bold font-mono ${sevColor(s)}`}>{sevCounts[s] ?? 0}</p>
                <p className="text-xs text-ink-muted uppercase mt-0.5">{s}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Findings list */}
        <section>
          <h2 className="text-sm font-semibold text-ink-secondary uppercase tracking-wider mb-3">
            Integrity Signals ({report?.findings?.length ?? 0})
          </h2>
          {(!report?.findings || report.findings.length === 0) ? (
            <p className="text-sm text-ink-muted italic">No integrity signals recorded for this session.</p>
          ) : (
            <ul className="divide-y divide-line">
              {report.findings.map((f, i) => (
                <li key={f.id ?? i} className="py-3 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink">{f.ruleId || f.findingType || f.message || f.type || 'Finding'}</p>
                    {(f.description || f.details || f.message) && <p className="text-xs text-ink-muted mt-0.5 truncate">{f.description || f.details || f.message}</p>}
                  </div>
                  <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold border shrink-0 ${sevBadge(f.severity)}`}>
                    {f.severity || 'INFO'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {report?.verdictSummary && (
          <section className="border-t border-line pt-5">
            <p className="text-sm text-ink italic border-l-2 border-teal-500 pl-3">
              {report.verdictSummary}
            </p>
          </section>
        )}

        <footer className="border-t border-line pt-4 text-center">
          <p className="text-xs text-ink-muted">
            This is a read-only public share link generated by the interview host.
          </p>
        </footer>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-canvas-subtle border border-line rounded-lg px-4 py-3">
      <p className="text-[11px] text-ink-muted uppercase tracking-wider font-semibold">{label}</p>
      <p className="font-heading text-lg font-bold text-ink mt-1">{value}</p>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="bg-canvas-subtle border border-line rounded-lg px-4 py-3">
      <p className="text-[11px] text-ink-muted uppercase tracking-wider font-semibold">{label}</p>
      <p className="text-sm text-ink mt-1 break-all">{value || '—'}</p>
    </div>
  );
}
